/**
 * RoomEntryBox.js
 *
 * Entry screen for a game, with two big buttons:
 * 1. Join a Game   -   opens a popup asking for the room code.
 * 2. Create a Game -   generates a room code and makes the user the host.
 *
 * Side by side on desktop, stacked on mobile.
 */

import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, LogIn, Plus, X } from "lucide-react";
import toast from "react-hot-toast";
import Button from "../../components/ui/Button";
import { fetchRoomDetails } from "../helpers/joinRoom";
import { createARoom } from "../helpers/createARoom";

const RoomEntryBox = ({ setRoom, user, dbCollection, gameName, onRoomCreated }) => {
    const [showJoinPopup, setShowJoinPopup] = useState(false);
    const [roomID, setRoomID] = useState("");
    const [isBusy, setIsBusy] = useState(false);

    /**
     * Fetches the room info and enters it
     */
    const joinRoom = async (e) => {
        e.preventDefault();
        if (!roomID.trim()) {
            toast.error("Please enter a room code first.");
            return;
        }

        setIsBusy(true);
        const res = await fetchRoomDetails(dbCollection, roomID.trim().toLowerCase());
        setIsBusy(false);

        if (res?.id) {
            toast.success("Room Joined");
            setRoom(res.id);
        } else {
            toast.error("Room does not exist");
        }
    };

    /**
     * Creates a new room with a generated code, with this user as the host
     */
    const createRoom = async () => {
        if (isBusy) return;

        setIsBusy(true);
        const res = await createARoom(user, dbCollection);
        if (res && onRoomCreated) await onRoomCreated(res);
        setIsBusy(false);

        if (res) {
            toast.success(`Room ${res.id.toUpperCase()} created`);
            setRoom(res.id);
        } else {
            toast.error("Unable to create a room");
        }
    };

    const closeJoinPopup = () => {
        setShowJoinPopup(false);
        setRoomID("");
    };

    const tileClasses = `
        relative w-full h-48 md:h-72 rounded-3xl p-6 md:p-8
        flex flex-col items-start justify-start text-left text-black
        shadow-xl transition-transform duration-300
        hover:scale-[1.02] active:scale-[0.98]
        focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60
        disabled:opacity-60 disabled:hover:scale-100
    `;

    return (
        <div className="w-full max-w-5xl px-6 pt-24 pb-12">
            {gameName && (
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-8">
                    {gameName}
                </h1>
            )}

            <div className="flex flex-col md:flex-row gap-6">
                <button
                    onClick={() => setShowJoinPopup(true)}
                    disabled={isBusy}
                    className={`${tileClasses} bg-gradient-to-br from-aqua via-blue to-lavender`}
                >
                    <span className="text-3xl md:text-4xl font-extrabold tracking-tight">Join a Game</span>
                    <span className="mt-2 text-black/70">Got a room code from a friend? Jump in.</span>
                    <LogIn className="absolute bottom-6 right-6 md:bottom-8 md:right-8 w-12 h-12 md:w-16 md:h-16 opacity-80" strokeWidth={1.5} />
                </button>

                <button
                    onClick={createRoom}
                    disabled={isBusy}
                    className={`${tileClasses} bg-gradient-to-br from-yellow via-peach to-coral`}
                >
                    <span className="text-3xl md:text-4xl font-extrabold tracking-tight">Create a Game</span>
                    <span className="mt-2 text-black/70">Start a new room and host it for your friends.</span>
                    <Plus className="absolute bottom-6 right-6 md:bottom-8 md:right-8 w-12 h-12 md:w-16 md:h-16 opacity-80" strokeWidth={1.5} />
                </button>
            </div>

            {/* --- Join popup --- */}
            <AnimatePresence>
                {showJoinPopup && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeJoinPopup}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-6"
                    >
                        <motion.form
                            initial={{ scale: 0.95, y: 10 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 10 }}
                            onClick={(e) => e.stopPropagation()}
                            onSubmit={joinRoom}
                            className="relative w-full max-w-md rounded-3xl border border-zinc-800 bg-zinc-950 p-8"
                        >
                            <button
                                type="button"
                                onClick={closeJoinPopup}
                                aria-label="Close"
                                className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
                            >
                                <X size={20} />
                            </button>

                            <h2 className="text-2xl font-bold text-white">Enter the room code</h2>
                            <p className="mt-1 text-sm text-zinc-400">Ask the host for the code shown on their screen.</p>

                            <input
                                type="text"
                                value={roomID}
                                onChange={(e) => setRoomID(e.target.value)}
                                onKeyDown={(e) => e.key === "Escape" && closeJoinPopup()}
                                placeholder="e.g. K7MQ2X"
                                autoFocus
                                autoCapitalize="characters"
                                autoComplete="off"
                                spellCheck={false}
                                className="mt-6 w-full rounded-xl border border-zinc-700 bg-black p-4 text-center text-2xl font-mono uppercase tracking-[0.3em] text-white placeholder:text-zinc-600 placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-blue"
                            />

                            <Button
                                type="submit"
                                variant="main"
                                size="lg"
                                disabled={isBusy}
                                className="mt-6 w-full gap-2 rounded-xl"
                            >
                                {isBusy ? "Joining..." : "Join"} <ArrowRight size={18} />
                            </Button>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RoomEntryBox;
